/**
 * Hook: G14 Quick Calculator
 *
 * Fetches pre-computed G14 stats via getG14QuickStats query (public, apiKey auth).
 * Computes estimated G14 cost based on consumption preset and monthly kWh.
 */

import { useState, useEffect, useMemo } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';
import {
  type ConsumptionPreset,
  getNormalizedWeights,
} from '../constants/consumptionPresets';

export interface G14QuickStats {
  year: number;
  dataMonths: number;
  hourlyAvgPriceKwh: number[];
  zoneDistribution: Record<string, number>;
  avgMonthlyRcePriceKwh: number;
  monthlyPriceRange: { min: number; max: number };
  lastUpdated: string;
}

/**
 * Zone-specific distribution rates (PLN/kWh, net) — 2026 TAURON G14
 * Source: TAURON Dystrybucja tariff schedule effective 2026-01-01
 * TODO: Replace with rates from getG14QuickStats response when backend provides them
 */
const ZONE_DISTRIBUTION_RATES: Record<string, number> = {
  GREEN: 0.0129,
  LIGHT_GREEN: 0.0524,
  YELLOW: 0.3851,
  RED: 2.5725,
};

/** Fixed monthly fees for G14 (PLN/month) */
const G14_FIXED_FEES = {
  fixedMonthlyFee: 7.68,
  meteringFee: 13.36,
  qualityCharge: 0.0351, // per kWh
};

/** VAT rate — comparison tariffs (G11/G12) are gross prices, G14 components are net */
const VAT_RATE = 1.23;

export interface G14QuickCalcResult {
  monthlyG14Cost: number;
  annualG14Cost: number;
  monthlyComparisonCost: number;
  annualComparisonCost: number;
  annualSavings: number;
  savingsPercent: number;
  recommendation: 'green' | 'yellow' | 'red';
  recommendationText: string;
  avgHourlyPrices: number[];
  zoneDistribution: Record<string, number>;
}

export interface UseG14QuickCalcReturn {
  stats: G14QuickStats | null;
  loading: boolean;
  error: string | null;
  calculate: (
    monthlyKwh: number,
    preset: ConsumptionPreset,
    comparisonTariffRate: number
  ) => G14QuickCalcResult | null;
}

export function useG14QuickCalc(year: number): UseG14QuickCalcReturn {
  const [stats, setStats] = useState<G14QuickStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      setLoading(true);
      setError(null);

      try {
        const client = getAmplifyClient();
        const response = await client.queries.getG14QuickStats({
          input: JSON.stringify({ year }),
        });

        if (cancelled) return;

        if (response.errors && response.errors.length > 0) {
          throw new Error(response.errors[0].message);
        }

        if (!response.data) {
          throw new Error('Brak danych z serwera');
        }

        const parsed = typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;

        if (!parsed.success) {
          setStats(null);
          setError(parsed.error || `Brak danych G14 dla roku ${year}`);
          return;
        }

        setStats({
          year: parsed.year,
          dataMonths: parsed.dataMonths,
          hourlyAvgPriceKwh: parsed.hourlyAvgPriceKwh,
          zoneDistribution: parsed.zoneDistribution,
          avgMonthlyRcePriceKwh: parsed.avgMonthlyRcePriceKwh,
          monthlyPriceRange: parsed.monthlyPriceRange,
          lastUpdated: parsed.lastUpdated,
        });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Nie udało się pobrać danych G14');
        setStats(null);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchStats();
    return () => { cancelled = true; };
  }, [year]);

  const calculate = useMemo(() => {
    return (
      monthlyKwh: number,
      preset: ConsumptionPreset,
      comparisonTariffRate: number
    ): G14QuickCalcResult | null => {
      if (!stats || !stats.hourlyAvgPriceKwh || stats.hourlyAvgPriceKwh.length !== 24) {
        return null;
      }

      const weights = getNormalizedWeights(preset);

      // Energy cost: sum of (weight[h] * avgPrice[h]) * monthlyKwh
      let monthlyEnergyCost = 0;
      for (let h = 0; h < 24; h++) {
        monthlyEnergyCost += weights[h] * stats.hourlyAvgPriceKwh[h] * monthlyKwh;
      }

      // Distribution cost: weighted average of zone distribution rates
      // Approximate: use zone distribution percentages to estimate avg distribution cost
      let avgDistributionRate = 0;
      for (const [zone, fraction] of Object.entries(stats.zoneDistribution)) {
        avgDistributionRate += (ZONE_DISTRIBUTION_RATES[zone] ?? 0.1509) * fraction;
      }
      const monthlyDistributionCost = monthlyKwh * avgDistributionRate;

      // Quality charge
      const monthlyQualityCost = monthlyKwh * G14_FIXED_FEES.qualityCharge;

      // Fixed fees
      const monthlyFixedCost = G14_FIXED_FEES.fixedMonthlyFee + G14_FIXED_FEES.meteringFee;

      const monthlyG14CostNet = monthlyEnergyCost + monthlyDistributionCost + monthlyQualityCost + monthlyFixedCost;
      const monthlyG14Cost = monthlyG14CostNet * VAT_RATE;
      const annualG14Cost = monthlyG14Cost * 12;

      // Comparison tariff (flat rate)
      // comparisonTariffRate is gross (includes VAT), but fixed fees are net — apply VAT
      const monthlyComparisonCost = monthlyKwh * comparisonTariffRate + monthlyFixedCost * VAT_RATE;
      const annualComparisonCost = monthlyComparisonCost * 12;

      const annualSavings = annualComparisonCost - annualG14Cost;
      const savingsPercent = annualComparisonCost > 0
        ? (annualSavings / annualComparisonCost) * 100
        : 0;

      // Traffic light recommendation
      let recommendation: 'green' | 'yellow' | 'red';
      let recommendationText: string;

      if (savingsPercent > 10) {
        recommendation = 'green';
        recommendationText = 'G14 jest korzystna!';
      } else if (savingsPercent >= 0) {
        recommendation = 'yellow';
        recommendationText = 'Rozważ ostrożnie';
      } else {
        recommendation = 'red';
        recommendationText = 'G14 nie jest dla Ciebie opłacalna';
      }

      return {
        monthlyG14Cost: Math.round(monthlyG14Cost * 100) / 100,
        annualG14Cost: Math.round(annualG14Cost),
        monthlyComparisonCost: Math.round(monthlyComparisonCost * 100) / 100,
        annualComparisonCost: Math.round(annualComparisonCost),
        annualSavings: Math.round(annualSavings),
        savingsPercent: Math.round(savingsPercent * 10) / 10,
        recommendation,
        recommendationText,
        avgHourlyPrices: stats.hourlyAvgPriceKwh,
        zoneDistribution: stats.zoneDistribution,
      };
    };
  }, [stats]);

  return { stats, loading, error, calculate };
}
